import { Typography } from 'antd';
import { calculateSagitta } from './calculations';
import type { UnitSystem } from './types';
import { UNIT_SUFFIX } from './types';

const { Text: AntText } = Typography;

export interface ArcDiagramProps {
	chord: number | null;
	radius: number | null;
	unit: UnitSystem;
}

const W = 400;
const H = 220;
const M = 32;

function clamp(n: number, lo: number, hi: number): number {
	return Math.max(lo, Math.min(hi, n));
}

export default function ArcDiagram({ chord, radius, unit }: ArcDiagramProps) {
	const suf = UNIT_SUFFIX[unit];
	const hasValues =
		chord != null &&
		radius != null &&
		chord > 0 &&
		radius > 0 &&
		chord < 2 * radius;
	const cVal = hasValues ? (chord as number) : null;
	const rVal = hasValues ? (radius as number) : null;
	const s = cVal != null && rVal != null ? calculateSagitta(rVal, cVal) : null;
	const sOk = s != null && !Number.isNaN(s) && s > 0;

	const cPx = 280;
	const half = cPx / 2;
	let sPx: number;
	let exaggerated = false;
	if (sOk && cVal != null && s != null) {
		const ratio = s / cVal;
		sPx = cPx * ratio * 2.2;
		if (sPx < 6) {
			sPx = 14;
			exaggerated = true;
		}
	} else {
		sPx = 18;
	}

	const cx = W / 2;
	const baseY = H - M - 10;
	const x1 = cx - half;
	const x2 = cx + half;
	const arcPeakY = baseY - sPx;

	const rLarge =
		rVal != null && cVal != null ? rVal * 2 > cVal * 8 : false;
	const showRadiusLine = hasValues && !rLarge;
	const Rpx = (half * half + sPx * sPx) / (2 * sPx) || 100;
	const cy = arcPeakY + Rpx;
	const largeArc = 0;

	const sagLabel =
		sOk && s != null ? `s = ${s.toFixed(6)} ${suf}` : 's (sagitta)';
	const cLabel = cVal != null ? `c = ${cVal.toFixed(4)} ${suf}` : 'c';
	const rLabel =
		rVal != null && !rLarge
			? `R = ${rVal.toFixed(4)} ${suf}`
			: rVal != null
				? `R = ${rVal.toFixed(2)} ${suf}`
				: 'R';

	return (
		<div className="w-full max-w-md mx-auto">
			<svg
				viewBox={`0 0 ${W} ${H}`}
				className="w-full h-auto border border-slate-200 rounded bg-white"
				role="img"
				aria-label="Arc, chord, and sagitta diagram"
			>
				<line
					x1={x1}
					y1={baseY + 1}
					x2={x2}
					y2={baseY + 1}
					stroke="#1e3a5f"
					strokeWidth="2"
				/>
				{hasValues ? (
					<path
						d={`M ${x1} ${baseY} A ${Rpx} ${Rpx} 0 ${largeArc} 1 ${x2} ${baseY}`}
						fill="none"
						stroke="#2563eb"
						strokeWidth="2.2"
						strokeLinecap="round"
					/>
				) : (
					<path
						d={`M ${x1} ${baseY} Q ${cx} ${arcPeakY} ${x2} ${baseY}`}
						fill="none"
						stroke="#2563eb"
						strokeWidth="2.2"
						strokeLinecap="round"
					/>
				)}
				<circle cx={x1} cy={baseY} r="3" fill="#0f172a" />
				<circle cx={x2} cy={baseY} r="3" fill="#0f172a" />
				{showRadiusLine && (
					<g opacity={0.45}>
						<line
							x1={cx}
							y1={cy}
							x2={cx}
							y2={arcPeakY}
							stroke="#cbd5e1"
							strokeWidth="0.7"
							strokeDasharray="5 5"
						/>
						<text
							x={cx + 4}
							y={clamp(cy - 8, 12, H - 8)}
							fontSize="9"
							fill="#94a3b8"
							className="font-sans"
						>
							{rLabel}
						</text>
					</g>
				)}
				{!showRadiusLine && hasValues && (
					<text
						x={x2 - 2}
						y={arcPeakY - 6}
						textAnchor="end"
						fontSize="9"
						fill="#94a3b8"
						className="font-sans"
					>
						{rLabel}
					</text>
				)}

				<line
					x1={cx}
					y1={baseY}
					x2={cx}
					y2={arcPeakY}
					stroke="#dc2626"
					strokeWidth="4.5"
					strokeLinecap="round"
				/>
				<polygon
					points={`${cx - 4},${arcPeakY} ${cx + 4},${arcPeakY} ${cx},${arcPeakY - 6}`}
					fill="#dc2626"
				/>
				<text
					x={cx + 8}
					y={(baseY + arcPeakY) / 2 - 2}
					fontSize="12"
					fontWeight="800"
					fill="#b91c1c"
					className="font-sans"
				>
					{sagLabel}
				</text>
				<text
					x={x1}
					y={baseY + 20}
					fontSize="10"
					fill="#0f172a"
					className="font-sans"
				>
					{cLabel}
				</text>
				<g>
					<line
						x1={x1 - 4}
						y1={baseY + 4}
						x2={x2 + 4}
						y2={baseY + 4}
						stroke="#64748b"
						strokeWidth="0.6"
						markerStart="url(#ar)"
						markerEnd="url(#ar)"
					/>
				</g>
				<defs>
					<marker
						id="ar"
						markerWidth="4"
						markerHeight="4"
						refX="2"
						refY="2"
						orient="auto"
					>
						<path d="M0,0 L4,2 L0,4" fill="#64748b" />
					</marker>
				</defs>
				<text
					x={cx - 32}
					y={H - 8}
					fontSize="10"
					fontWeight="600"
					fill="#b91c1c"
					className="font-sans"
				>
					Measure this distance
				</text>
				<line
					x1={cx - 20}
					y1={H - 18}
					x2={cx - 1}
					y2={(baseY + arcPeakY) / 2 + 2}
					stroke="#dc2626"
					strokeWidth="0.6"
					strokeDasharray="2 2"
				/>
			</svg>
			{exaggerated ? (
				<AntText type="secondary" className="text-xs block text-center mt-1">
					Diagram exaggerated for clarity
				</AntText>
			) : null}
		</div>
	);
}
